import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { swatidService } from '@/services';
import type { ApiSwatidSchedule } from '@/types/api';
import { SWATID_DAYS, SWATID_DAY_LABELS } from '@/constants/swatid';
import { isValidSwatidTime } from '@/utils/swatidValidation';
import { useSwatidRefresh } from '@/hooks/useSwatidController';
import { useToast } from '@/hooks/use-toast';

const schema = z
  .object({
    relayNumber: z.coerce.number().min(1).max(16),
    day: z.string().min(1),
    startTime: z.string().regex(/^\d{2}:\d{2}$/, 'Formato HH:MM'),
    endTime: z.string().regex(/^\d{2}:\d{2}$/, 'Formato HH:MM'),
    slot: z.coerce.number().min(0).max(4).optional(),
  })
  .refine((d) => isValidSwatidTime(d.startTime), {
    message: 'Hora inicio: múltiplos de 15 min',
    path: ['startTime'],
  })
  .refine((d) => isValidSwatidTime(d.endTime), {
    message: 'Hora fin: múltiplos de 15 min',
    path: ['endTime'],
  })
  .refine((d) => d.endTime > d.startTime, {
    message: 'La hora fin debe ser posterior al inicio',
    path: ['endTime'],
  });

type FormData = z.infer<typeof schema>;

interface SwatidScheduleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controllerId: string;
  defaultRelayNumber?: number;
  defaultDay?: string;
  /** Si se proporciona, al guardar se elimina y se recrea (editar) */
  replaceSchedule?: ApiSwatidSchedule | null;
}

export function SwatidScheduleFormDialog({
  open,
  onOpenChange,
  controllerId,
  defaultRelayNumber = 1,
  defaultDay = 'lunes',
  replaceSchedule,
}: SwatidScheduleFormDialogProps) {
  const { toast } = useToast();
  const { refreshAll } = useSwatidRefresh(controllerId);
  const isReplace = !!replaceSchedule;

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      relayNumber: defaultRelayNumber,
      day: 'lunes',
      startTime: '08:00',
      endTime: '08:30',
    },
  });

  useEffect(() => {
    if (!open) return;
    if (replaceSchedule) {
      form.reset({
        relayNumber: replaceSchedule.relayNumber,
        day: replaceSchedule.dayName,
        startTime: replaceSchedule.startTime,
        endTime: replaceSchedule.endTime,
        slot: replaceSchedule.slotNumber,
      });
    } else {
      form.reset({
        relayNumber: defaultRelayNumber,
        day: defaultDay,
        startTime: '08:00',
        endTime: '08:30',
      });
    }
  }, [open, replaceSchedule, defaultRelayNumber, defaultDay, form]);

  const saveMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (isReplace && replaceSchedule) {
        const del = await swatidService.deleteSchedule(controllerId, replaceSchedule.id);
        if (!del.success) {
          throw new Error(del.error?.message || 'Error al eliminar programación anterior');
        }
      }
      const res = await swatidService.createSchedule(controllerId, {
        relayNumber: data.relayNumber,
        day: data.day,
        slot: data.slot,
        startTime: data.startTime,
        endTime: data.endTime,
      });
      if (!res.success) throw new Error(res.error?.message || 'Error al crear programación');
      return res.data;
    },
    onSuccess: async () => {
      await refreshAll(1000);
      toast({
        title: isReplace ? 'Programación actualizada' : 'Programación creada',
        description: 'Programación guardada. Pendiente de confirmación en el dispositivo.',
      });
      onOpenChange(false);
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isReplace ? 'Editar programación' : 'Nueva programación'}
          </DialogTitle>
          <DialogDescription>
            Horarios en múltiplos de 15 min. Máx. 5 por relé y día.
            {isReplace && ' (Se reemplazará la programación actual)'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((d) => saveMutation.mutate(d))}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="relayNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Relé (1-16)</FormLabel>
                  <FormControl>
                    <Input type="number" min={1} max={16} {...field} disabled={isReplace} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="day"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Día</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={isReplace}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      {SWATID_DAYS.map((d) => (
                        <option key={d} value={d}>
                          {SWATID_DAY_LABELS[d]}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Inicio</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="08:00" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fin</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="08:30" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
